import React from 'react';
import { Box, Button, Flex, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { CampaignType, FoodfluenceLocation } from '@foodfluence/shared';
import { campaignTypeDisplayNames } from '../../../types';
import { LOGISTICS_META } from './UgcDeliverablesPicker';
import UgcPieceIcon from './UgcPieceIcon';
import { UGC_RATE_PRESETS } from '../../../utils/contentUtils';
import { ff } from '../../../theme';

interface ProposalReviewSummaryProps {
  campaignType: CampaignType;
  /** UGC proposals: preset displayName -> count */
  ugcPresetCounts?: Record<string, number>;
  /** Influencer proposals: social deliverable lines */
  socialDeliverables?: { label: string; count: number }[];
  selectedLocations?: FoodfluenceLocation[];
  cashAmount: number;
  creditAmount: number;
  showCredit: boolean;
  onEditStep: (step: number) => void;
}

const Divider = () => <Box borderTopWidth="1px" borderColor={ff.line} />;

const SummaryRow: React.FC<{
  label: string;
  step: number;
  onEditStep: (step: number) => void;
  children: React.ReactNode;
}> = ({ label, step, onEditStep, children }) => (
  <Box py={3}>
    <HStack justify="space-between" align="center" mb={1}>
      <Text textStyle="eyebrow" color={ff.text2}>
        {label}
      </Text>
      <Button
        variant="ghost"
        size="sm"
        h="44px"
        mr={-3}
        my={-2.5}
        color={ff.ink}
        fontWeight="600"
        textDecoration="underline"
        textUnderlineOffset="3px"
        _hover={{ bg: 'transparent' }}
        onClick={() => onEditStep(step)}
      >
        Edit
      </Button>
    </HStack>
    {children}
  </Box>
);

const ProposalReviewSummary: React.FC<ProposalReviewSummaryProps> = ({
  campaignType,
  ugcPresetCounts,
  socialDeliverables,
  selectedLocations,
  cashAmount,
  creditAmount,
  showCredit,
  onEditStep,
}) => {
  const logistics = LOGISTICS_META[campaignType];
  const ugcLines = Object.entries(ugcPresetCounts ?? {}).filter(([, count]) => count > 0);
  const socialLines = (socialDeliverables ?? []).filter(({ count }) => count > 0);
  const totalAsk = cashAmount + (showCredit ? creditAmount : 0);

  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor={ff.line}
      borderRadius="20px"
      overflow="hidden"
      px={4}
    >
      <VStack spacing={0} align="stretch" divider={<Divider />}>
        <SummaryRow label="Collab" step={0} onEditStep={onEditStep}>
          <HStack spacing={2}>
            {logistics && <Icon as={logistics.icon} color={ff.text2} boxSize={3.5} />}
            <Text fontSize="15px" color={ff.ink} fontWeight="600">
              {campaignTypeDisplayNames[campaignType]}
            </Text>
          </HStack>
        </SummaryRow>

        {(selectedLocations?.length ?? 0) > 0 && (
          <SummaryRow label="Locations" step={0} onEditStep={onEditStep}>
            <VStack spacing={0.5} align="stretch">
              {selectedLocations!.slice(0, 3).map(location => (
                <Text key={location.docId} fontSize="15px" color={ff.body} noOfLines={1}>
                  {location.name || `${location.streetNumber} ${location.streetName}`}
                </Text>
              ))}
              {selectedLocations!.length > 3 && (
                <Text fontSize="13px" color={ff.text2}>+{selectedLocations!.length - 3} more</Text>
              )}
            </VStack>
          </SummaryRow>
        )}

        <SummaryRow label="Deliverables" step={1} onEditStep={onEditStep}>
          <VStack spacing={0.5} align="stretch">
            {ugcLines.map(([name, count]) => {
              const preset = UGC_RATE_PRESETS.find(p => p.displayName === name);
              const [title, spec] = name.split(/\s*(?=\()/);
              return (
                <HStack key={name} spacing={2.5} py={1}>
                  {preset && <UgcPieceIcon type={preset.type as 'video' | 'photo'} size={24} />}
                  <Text fontSize="15px" color={ff.body}>
                    {count}× {title}
                    {spec && <Text as="span" color={ff.muted}> {spec}</Text>}
                  </Text>
                </HStack>
              );
            })}
            {socialLines.map(({ label, count }) => (
              <Text key={label} fontSize="15px" color={ff.body}>
                {count}× {label}
              </Text>
            ))}
            {ugcLines.length === 0 && socialLines.length === 0 && (
              <Text fontSize="15px" color={ff.muted}>None selected</Text>
            )}
          </VStack>
        </SummaryRow>

        <SummaryRow label="Compensation" step={2} onEditStep={onEditStep}>
          <VStack spacing={0.5} align="stretch">
            {showCredit && (
              <HStack justify="space-between">
                <Text fontSize="15px" color={ff.text2}>Credit</Text>
                <Text fontSize="15px" color={ff.ink} fontWeight="600">${creditAmount}</Text>
              </HStack>
            )}
            <HStack justify="space-between">
              <Text fontSize="15px" color={ff.text2}>Cash</Text>
              <Text fontSize="15px" color={ff.ink} fontWeight="600">${cashAmount}</Text>
            </HStack>
          </VStack>
        </SummaryRow>

        <Flex align="center" justify="space-between" mx={-4} px={4} minH="56px" bg={ff.limeSurface}>
          <Text fontSize="15px" color={ff.ink} fontWeight="600">Your ask</Text>
          <Text textStyle="display" fontSize="22px" color={ff.ink}>${totalAsk}</Text>
        </Flex>
      </VStack>
    </Box>
  );
};

export default ProposalReviewSummary;
